import hyperdom from 'hyperdom'
import datePicker from '../../../datePicker.js'
import LocalDate from '@booking-services/localDate'
import * as validation from '../../../validation.js'
import ExtraStays from './extraStays.jsx'
import additionalTourFormSchema from './additionalTourFormSchema.js'
import * as gtmDataLayer from '../../../analytics/gtm/dataLayer.js'

export default class ExtrasTab {
  constructor ({
    config,
    quoteCriteria,
    promisedFlights,
    flights,
    departure,
    modals,
    next,
  }) {
    this.config = config
    this.promisedFlights = promisedFlights
    this.flights = flights
    this.quoteCriteria = quoteCriteria
    this.next = next
    this.departure = departure
    this.additionalTourFormSchema = additionalTourFormSchema(config)

    this.extraStays = new ExtraStays({
      departure: departure,
      modals: modals,
      quoteCriteria: quoteCriteria,
      flights: flights,
      promisedFlights: promisedFlights,
    })
  }

  onadd () {
    gtmDataLayer.activateOptimize()
  }

  onupdate () {
    gtmDataLayer.activateOptimize()
  }

  renderAdditionalTour () {
    const dateFormat = this.config.dateFormat

    return (
      <div class='additional-tour'>
        <div className='additional-tour__header'>
          <h2>Additional trips</h2>
          <span>Optional</span>
        </div>
        <div class='o-accent-box'>
          <div class='summary'>
            <label for={`additional-tour-requested`} className='o-choice-item -fullWidth u-margin-bottom-none'>
              <input id={`additional-tour-requested`} type='checkbox'
                className='o-choice-item__checkbox o-choice-item__checkbox-small'
                binding="this.quoteCriteria.additionalTourRequested"/>
              <div className='o-choice-item__content o-choice-item__content_small'>
                <span>Would you like to book more than one {this.config.brandName} trip today?</span>
              </div>
            </label>
            <div className={["question-container",{'show-expanded': this.quoteCriteria.additionalTourRequested}]}>
              <label for='additional-tour-name'>Provide details of any other trips and we'll be in touch to help.</label>
              <div class='additional-tour-date'>
                { datePicker(<input class='o-form-item__txt-input additional-tour-date-input'
                  onblur={() => this.additionalTourFieldVisited(this.quoteCriteria.additionalTour, 'date')}
                  type='text'
                  id='additional-tour-date'
                  readOnly
                  placeholder={dateFormat} />,
                {
                  binding: [this.quoteCriteria.additionalTour, 'date'],
                  format: dateFormat,
                  parseFormat: dateFormat,
                  defaultDate: new Date(),
                  minDate: new Date(),
                }
                )}
                { this.renderValidationError(this.quoteCriteria.additionalTour, 'date') }
              </div>

              <div class='additional-tour-name'>
                <input id='additional-tour-name'
                  onblur={() => this.additionalTourFieldVisited(this.quoteCriteria.additionalTour, 'name')}
                  placeholder='Trip name'
                  class='additional-tour-name-input o-form-item__txt-input u-margin-top-small'
                  binding={[this.quoteCriteria.additionalTour, 'name']} />
                { this.renderValidationError(this.quoteCriteria.additionalTour, 'name') }
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  render () {
    const {additionalTrips, prePostAccommodation} = this.config.features.extras
    return <div class='extras' data-test="extras-tab">
      <form novalidate focusable-form name='form.extrasForm' id='extrasForm' data-id='flight-search'
        onsubmit={(ev) => {
          ev.preventDefault()
          this.next()
        }} class='u-clearfix'>
        { prePostAccommodation ? this.extraStays : undefined }
        { this.promisedFlights.offerPromisedFlights() ? this.promisedFlights : undefined }
        { this.offerFlights() ? this.flights : undefined }
        { additionalTrips ? this.renderAdditionalTour() : undefined }
      </form>
    </div>
  }

  additionalTourFieldVisited (model, property) {
    this.validation = validation.fieldVisited(this.quoteCriteria.additionalTour, this.additionalTourFormSchema, model, property)
  }

  validate () {
    if(!this.quoteCriteria.additionalTourRequested) {
      return {valid: true}
    }
    this.validation = validation.validate(this.quoteCriteria.additionalTour, this.additionalTourFormSchema)
    this.showValidationErrors = !this.validation.valid
    return this.validation
  }

  offerFlights () {
    const config = this.config
    const departure = this.departure
    const isFlightSuppressed = (config.flights.brochuresFlightSuppression || []).includes(departure.brochureCode)
    const isFlightEnabled = ['enabled', 'callback'].indexOf(config.flights.mode) !== -1
    return !isFlightSuppressed && isFlightEnabled && !this.promisedFlights.offerPromisedFlights()
  }

  offerPromisedFlights () {
    const config = this.config
    const departure = this.departure
    const hasBrochurePromisedFlights = (config.promisedFlights.brochuresWithPromisedFlights || []).includes(departure.brochureCode)

    return hasBrochurePromisedFlights && this.isDepartureEligibleForPromisedFlights()
  }

  isDepartureEligibleForPromisedFlights () {
    const config = this.config
    const departure = this.departure

    const startDate = departure.originDepartureDate ? departure.originDepartureDate : departure.startDate
    const nights = startDate.differenceInDays(departure.endDate)
    const hasMinimumRequiredNights = nights >= (config.promisedFlights.minimumTripDurationInNights || 0)

    const numberOfDaysToDeparture = new LocalDate().differenceInDays(startDate)
    const hasMinimumRequiredDaysBeforeDeparture = numberOfDaysToDeparture >= (config.promisedFlights.minimumLeastNumberOfDaysBeforeDeparture || 0)

    return hasMinimumRequiredNights && hasMinimumRequiredDaysBeforeDeparture
  }

  renderValidationError (model, property) {
    if (this.showValidationErrors || (model._visited && model._visited.indexOf(property) != -1)) {
      var error = this.validation.error(model, property)
      if (error) {
        return <div class='o-form-item__warning'>
          <span>{error}</span>
        </div>
      }
    }
  }
}
